import { Component, OnInit } from '@angular/core';
import { finalize } from 'rxjs/operators';
import { AppToastService } from 'src/app/shared/services/app-toast.service';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { AdminService } from 'src/app/core/services/admin.service';
import { TenantRoleService } from 'src/app/core/services/tenant-role.service';
import { TableLazyLoadEvent } from 'primeng/table';

// Services
import { TenantService } from 'src/app/core/services/tenant.service';

// Models & Config
import { Tenant } from 'src/app/core/models/tenant';
import {
  TableAction,
  TableColumn,
} from 'src/app/shared/models/table-config.model';

// Components
import { TenantModalComponent } from './tenant-modal/tenant-modal.component';
import { RolePickerComponent } from 'src/app/shared/components/role-picker/role-picker.component';
import { ConfirmationModalComponent } from 'src/app/shared/components/confirmation-modal/confirmation-modal.component';
import { TenantPreviewModalComponent } from './tenant-preview-modal/tenant-preview-modal.component';
import { AuditHistoryModalComponent } from 'src/app/shared/components/audit-history-modal/audit-history-modal.component';

@Component({
  selector: 'app-tenant',
  templateUrl: './tenant.component.html',
  styleUrls: ['./tenant.component.scss'],
  standalone: false,
  providers: [DialogService],
})
export class TenantComponent implements OnInit {
  // Dados
  allTenants: any[] = [];
  visibleTenants: any[] = [];
  tableData: any[] = [];
  totalRecords: number = 0;
  loading: boolean = true;
  searchText: string = '';

  ref: DynamicDialogRef | undefined;

  // Configuração de Status Visual
  private readonly styleActive = {
    label: 'Active',
    cssClass: 'text-[var(--primary-500)] bg-[var(--primary-500)]/15 font-bold',
  };
  private readonly styleInactive = {
    label: 'Inactive',
    cssClass: 'text-[var(--primary-500)]/50 bg-[var(--primary-500)]/5 font-bold',
  };

  // Colunas
  tableColumns: TableColumn[] = [
    {
      field: 'id',
      header: 'ID',
      type: 'text',
      width: '80px',
      sortable: true,
      linkAction: (row) => this.openTenantModal(row.id),
    },
    {
      field: 'cnpj',
      header: 'CNPJ',
      type: 'text',
      width: '15%',
      sortable: true,
    },
    {
      field: 'name',
      header: 'NAME',
      type: 'text',
      width: '15%',
      sortable: true,
    },
    {
      field: 'subdomain',
      header: 'SUBDOMAIN',
      type: 'text',
      width: '10%',
      sortable: true,
    },

    // Colunas Visuais
    {
      field: 'primaryColor',
      header: 'COLOR',
      type: 'color',
      width: '80px',
      sortable: false,
    },
    {
      field: 'logo',
      header: 'LOGO',
      type: 'text',
      width: '20%',
      sortable: true,
    },

    {
      field: 'bucketAws',
      header: 'BUCKET URL',
      type: 'text',
      width: '15%',
      sortable: true,
    },
    {
      field: 'status',
      header: 'ACTIVE?',
      type: 'status',
      width: '100px',
      sortable: true,
      statusConfig: { YES: this.styleActive, NO: this.styleInactive },
    },
    { field: 'actions', header: '', type: 'actions', width: '120px' },
  ];

  // Ações da Tabela
  tableActions: TableAction[] = [
    {
      label: 'Preview',
      icon: 'pi pi-eye',
      styleClass: 'p-button-text p-button-success', // Botão verdinho pra dar destaque
      command: (row) => this.openPreviewModal(row),
    },
    {
      label: 'Manage Roles',
      icon: 'pi pi-id-card',
      styleClass: 'p-button-text p-button-info',
      command: (row) => this.editRolesModal(row),
    },
    {
      label: 'Edit',
      icon: 'pi pi-pencil',
      command: (row) => this.openTenantModal(row.id),
    },
    {
      label: 'History',
      icon: 'pi pi-history',
      styleClass: 'p-button-text p-button-rounded text-gray-500 hover:bg-gray-50',
      command: (row) => this.openAuditHistory(row),
    },
    {
      label: 'Delete',
      icon: 'pi pi-trash',
      styleClass: 'p-button-text p-button-danger',
      command: (row) => this.deleteTenant(row),
    },
  ];

  constructor(
    private tenantService: TenantService,
    private readonly toastService: AppToastService,
    private dialogService: DialogService,
    private adminService: AdminService,
    private tenantRoleService: TenantRoleService,
  ) {}

  ngOnInit(): void {
    this.getTenants();
  }

  // --- Carregamento de Dados ---

  getTenants() {
    this.loading = true;
    this.tenantService
      .getTenants(1000, 0, '')
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: any) => {
          const tenants = data?.tenants || [];

          this.allTenants = tenants.map((t: Tenant) => ({
            ...t,
            cnpj: this.formatCnpj(t.cnpj),
            status: t.active ? 'YES' : 'NO',

            logo: t.logoFileName || this.formatLogoForDisplay(t.logo),
          }));

          this.totalRecords = data?.tenantsCount || this.allTenants.length;
          this._updateTableData();
        },
        error: () => {},
      });
  }

  openPreviewModal(tenant: any) {
    this.ref = this.dialogService.open(TenantPreviewModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '12px', overflow: 'hidden' },
      baseZIndex: 10000,
      data: {
        tenantId: tenant.id,
        primaryColor: tenant.primaryColor,
      },
    });
  }

  openAuditHistory(tenant: any) {
    this.dialogService.open(AuditHistoryModalComponent, {
      showHeader: false,
      width: '600px',
      contentStyle: { overflow: 'visible', padding: '0', borderRadius: '12px' },
      data: {
        entityType: 'Tenant',
        entityId: tenant.id,
        title: `Tenant History — ${tenant.name}`,
        fieldLabels: [
          { field: 'Id', label: 'ID' },
          { field: 'Name', label: 'Name' },
          { field: 'CNPJ', label: 'CNPJ' },
          { field: 'Subdomain', label: 'Subdomain' },
          { field: 'LogoFileName', label: 'Logo File' },
          { field: 'PrimaryColor', label: 'Primary Color' },
          { field: 'BucketAws', label: 'Bucket URL' },
          { field: 'LGPD', label: 'LGPD' },
          { field: 'Active', label: 'Active' },
        ],
        fieldFormatters: {
          Active: (v: any) =>
            v === true || v === 'true' || v === 1 ? 'Yes' : 'No',
        },
      },
    });
  }

  private formatCnpj(value: string | undefined): string {
    if (!value) return '';
    const digits = value.replace(/\D/g, '');
    if (digits.length !== 14) return value;
    return digits.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5');
  }

  formatLogoForDisplay(logo: string | undefined): string {
    if (!logo) return '';
    if (logo.length > 100) return '(Image Data Loaded)';
    return logo;
  }

  // --- Lógica de Tabela ---

  handleSearch(text: string) {
    this.searchText = text?.toLowerCase() || '';
    this._updateTableData();
  }

  private _updateTableData(): void {
    const filtered = this.searchText
      ? this.allTenants.filter((t) => this.matchesFilter(t))
      : [...this.allTenants];
    this.tableData = filtered;
  }

  loadTenantsLazy(event: TableLazyLoadEvent) {
    if (!this.allTenants.length) return;
    this.processLocalData(event);
  }

  processLocalData(event: TableLazyLoadEvent) {
    this.loading = true;
    let filtered = this.allTenants;

    if (this.searchText) {
      filtered = this.allTenants.filter((t) => this.matchesFilter(t));
    }
    this.totalRecords = filtered.length;

    if (event.sortField) {
      filtered.sort((a, b) => {
        const valueA = a[event.sortField as string];
        const valueB = b[event.sortField as string];
        const strA = String(valueA || '').toLowerCase();
        const strB = String(valueB || '').toLowerCase();
        return (event.sortOrder || 1) * strA.localeCompare(strB);
      });
    }

    const first = event.first || 0;
    const rows = event.rows || 10;

    setTimeout(() => {
      this.visibleTenants = filtered.slice(first, first + rows);
      this.loading = false;
    }, 100);
  }

  private matchesFilter(data: any): boolean {
    const searchStr = this.searchText;
    return (
      (data.name && data.name.toLowerCase().includes(searchStr)) ||
      String(data.id ?? '').toLowerCase().includes(searchStr) ||
      (data.cnpj && data.cnpj.includes(searchStr)) ||
      (data.subdomain && data.subdomain.toLowerCase().includes(searchStr))
    );
  }

  // --- CRUD Operations ---

  openTenantModal(tenantId?: string) {
    this.ref = this.dialogService.open(TenantModalComponent, {
      showHeader: false,
      width: '800px',
      contentStyle: { overflow: 'auto', padding: '0' },
      baseZIndex: 10000,
      data: { tenantId: tenantId },
    });

    this.ref.onClose.subscribe((result: any) => {
      if (result?.status === 'success' || result === 'success') {
        this.getTenants();
      }
    });
  }

  editRolesModal(tenant: Tenant) {
    const selectedInitial: any[] = [];
    const availableInitial: any[] = [];

    this.ref = this.dialogService.open(RolePickerComponent, {
      showHeader: false,
      closable: true,
      width: '800px',
      styleClass: 'responsive-modal',
      contentStyle: { 'max-height': '85vh', overflow: 'hidden', padding: '0', 'border-radius': '12px' },
      baseZIndex: 10000,
      data: { title: `Manage Roles: ${tenant.name}`, availableItems: availableInitial, selectedItems: selectedInitial, displayField: 'label', idField: 'id' },
    });

    let childInstance: any;
    this.ref.onChildComponentLoaded?.subscribe((comp: any) => {
      childInstance = comp as any;
      childInstance.loading = true;
      childInstance.availableItems = availableInitial;
      childInstance.selectedItems = selectedInitial;
      childInstance.normalizeInputs();
    });

    // Fetch tenant and roles in parallel, populate when ready
    this.tenantService
      .getTenant(tenant.id)
      .pipe(finalize(() => (this.loading = false)))
      .subscribe({
        next: (data: Tenant) => {
          // fetch all roles then populate
          this.adminService.getRolesFull().subscribe({
            next: (allRoles: any[]) => {
              // Mapa por id para reaproveitar o rótulo amigável (DisplayName + URL)
              // tanto na coluna "Available" quanto na "Selected".
              const rolesById = new Map<string, any>(
                (allRoles || []).map((r: any) => [String(r.id), r]),
              );

              // Roles atualmente vinculadas ao tenant que ainda existem (Exists = true);
              // removidas não vêm de getRolesFull, então saem da listagem.
              const assignedRoles = (data.roles || []).filter((t: any) =>
                rolesById.has(String(t.id)),
              );
              const assignedIds = new Set<string>(
                assignedRoles.map((t: any) => String(t.id)),
              );

              const available = (allRoles || [])
                .filter((r: any) => !assignedIds.has(String(r.id)))
                .map((r: any) => ({ ...r, selected: false }));

              const selected = assignedRoles.map((t: any) => ({
                ...rolesById.get(String(t.id)),
                selected: false,
              }));

              if (childInstance) {
                childInstance.availableItems = available;
                childInstance.selectedItems = selected;
                childInstance.normalizeInputs();
                childInstance.loading = false;
                childInstance.takeSnapshot();
              }
            },
            error: () => {
              if (childInstance) childInstance.loading = false;
            },
          });
        },
        error: () => {
          if (childInstance) childInstance.loading = false;
        },
      });

    this.ref.onClose.subscribe((result: any) => {
      const roleIds = result?.roleNames;
      if (roleIds) {
        // Need tenant data id; fetch tenant again or capture id earlier
        const payload = { tenantId: tenant.id, roleIds };
        this.tenantRoleService.updateTenantRoles(payload).subscribe({
          next: () => {
            this.toastService.success('Roles Updated', 'Tenant roles updated successfully.');
            this.getTenants();
          },
          error: () => {},
        });
      }
    });
  }

  // --- DELETE COM MODAL PADRÃO ---
  deleteTenant(tenant: any) {
    const ref = this.dialogService.open(ConfirmationModalComponent, {
      showHeader: false,
      width: '450px',
      contentStyle: { padding: '0', borderRadius: '8px' },
      data: {
        title: `Delete ${tenant.name}?`,
        message: 'This action cannot be undone.',
        confirmLabel: 'Delete',
      },
    });

    ref.onClose.subscribe((confirmed) => {
      if (confirmed) {
        this.loading = true;
        this.tenantService.deleteTenant(tenant.id).subscribe({
          next: () => {
            this.toastService.success(
              'Deleted',
              'Tenant deleted successfully.',
            );
            this.allTenants = this.allTenants.filter((t) => t.id !== tenant.id);
            this.handleSearch(this.searchText);
            this.loading = false;
          },
          error: () => {
            this.loading = false;
          },
        });
      }
    });
  }
}
